import { useEffect, useRef, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

// Generic list + form for simple registries (insumos, fornecedores)
export function Registry({ endpoint, fields, columns, empty, title, canEdit, canDelete, testId, wide = false }) {
  const [list, setList] = useState([]);
  const [edit, setEditState] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const last = useRef({});
  const setEdit = setEditState;
  const open = (x) => { last.current = Object.fromEntries(fields.map((f) => [f.key, x[f.key] ?? ""])); setEditState(x); };
  const blur = async (f) => {
    const v = edit?.[f.key] ?? "";
    if (!f.onBlur || last.current[f.key] === v) return;
    last.current[f.key] = v;
    setBusyKey(f.key);
    const patch = await f.onBlur(v, edit);
    setBusyKey("");
    if (patch) setEdit((x) => (x ? { ...x, ...patch } : x));
  };
  const load = useCallback(() => api.get(endpoint).then((r) => setList(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const body = Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? Number(edit[f.key]) || 0 : edit[f.key] ?? ""]));
    try {
      edit.id ? await api.put(`${endpoint}/${edit.id}`, body) : await api.post(endpoint, body);
      toast.success("Salvo");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (x) => {
    if (!window.confirm(`Excluir ${x.name}?`)) return;
    try { await api.delete(`${endpoint}/${x.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {canEdit && <Button onClick={() => open(empty)} data-testid={`${testId}-new-btn`}><Plus className="h-4 w-4 mr-1" />{title}</Button>}
      <div className="card-surface overflow-x-auto">
        <Table data-testid={`${testId}-table`}>
          <TableHeader><TableRow>{columns.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}<TableHead /></TableRow></TableHeader>
          <TableBody>
            {list.map((x) => (
              <TableRow key={x.id} data-testid={`${testId}-row-${x.id}`}>
                {columns.map((c) => <TableCell key={c.label}>{c.render(x)}</TableCell>)}
                <TableCell className="text-right whitespace-nowrap">
                  {canEdit && <Button size="icon" variant="ghost" onClick={() => open(x)} data-testid={`${testId}-edit-${x.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(x)} data-testid={`${testId}-delete-${x.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={columns.length + 1} className="py-8 text-center text-muted-foreground">Nenhum registro.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className={wide ? "max-h-[92vh] overflow-y-auto sm:max-w-2xl" : ""} data-testid={`${testId}-modal`}>
          <DialogHeader><DialogTitle>{edit?.id ? "Editar" : title}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <Field key={f.key} label={f.label} hint={busyKey === f.key ? "Buscando dados…" : f.hint} className={f.wide ? "col-span-2" : ""}>
                  {f.options ? (
                    <select value={edit[f.key] ?? ""} onChange={(e) => setEdit((x) => ({ ...x, [f.key]: e.target.value }))} className="h-10 rounded-md border bg-transparent px-2 text-sm" data-testid={`${testId}-${f.key}-input`}>
                      {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : <Input type={f.type || "text"} step="0.001" value={edit[f.key] ?? ""} onChange={(e) => setEdit((x) => ({ ...x, [f.key]: e.target.value }))}
                    onBlur={() => blur(f)} onKeyDown={(e) => { if (f.onBlur && e.key === "Enter") { e.preventDefault(); blur(f); } }} disabled={busyKey === f.key} data-testid={`${testId}-${f.key}-input`} />}
                </Field>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={!edit?.name} data-testid={`${testId}-save-btn`}>Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
